// Locations CRUD (BUILD_SPEC.md §5.2) — services/api/src/modules/config/
// locations.ts. Silo calibration is a nested resource on a SILO-type location,
// but that config editor is G22's sole ownership (plan.md QC fix #3, see
// features/silo-counts/components/CalibrationForm.tsx) — NOT duplicated here.
// ADMIN write; read-only for other roles (see permissions.ts).
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconPlus } from '@tabler/icons-react';
import { LOCATION_TYPES, type LocationCreateInput, type LocationType } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useIsAdmin } from '../permissions';
import { useCreateLocation, useLocations, useSites, useUpdateLocation } from '../api';
import type { LocationDto } from '../types';

interface LocationFormValues {
  siteId: string;
  code: string;
  name: string;
  type: LocationType;
  parentId: string;
  isActive: boolean;
}

function toFormValues(defaultSiteId: string, row?: LocationDto): LocationFormValues {
  return {
    siteId: row?.siteId ?? defaultSiteId,
    code: row?.code ?? '',
    name: row?.name ?? '',
    type: row?.type ?? 'WAREHOUSE',
    parentId: row?.parentId ?? '',
    isActive: row?.isActive ?? true,
  };
}

function toInput(values: LocationFormValues): LocationCreateInput {
  return {
    siteId: values.siteId,
    code: values.code.trim(),
    name: values.name.trim(),
    type: values.type,
    parentId: values.parentId || null,
    isActive: values.isActive,
  };
}

function LocationFormModal({
  opened,
  onClose,
  editing,
  locations,
}: {
  opened: boolean;
  onClose: () => void;
  editing: LocationDto | null;
  locations: LocationDto[];
}) {
  const { data: sitesPage } = useSites();
  const sites = sitesPage?.items ?? [];
  const create = useCreateLocation();
  const update = useUpdateLocation(editing?.id ?? '');
  const mutation = editing ? update : create;

  const form = useForm<LocationFormValues>({
    initialValues: toFormValues(sites[0]?.id ?? '', editing ?? undefined),
    validate: {
      siteId: (v) => (v ? null : 'Site is required'),
      code: (v) => (v.trim() ? null : 'Code is required'),
      name: (v) => (v.trim() ? null : 'Name is required'),
    },
  });

  function handleSubmit(values: LocationFormValues) {
    mutation.mutate(toInput(values), { onSuccess: () => onClose() });
  }

  const siteOptions = sites.map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }));
  const parentOptions = locations
    .filter((l) => l.id !== editing?.id)
    .map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }));

  return (
    <Modal opened={opened} onClose={onClose} title={editing ? `Edit location ${editing.code}` : 'New location'} centered>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          {mutation.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {mutation.error instanceof ApiRequestError ? mutation.error.message : 'Failed to save location.'}
            </Alert>
          )}
          <Select label="Site" data={siteOptions} required searchable {...form.getInputProps('siteId')} />
          <TextInput label="Code" required {...form.getInputProps('code')} />
          <TextInput label="Name" required {...form.getInputProps('name')} />
          <Select
            label="Type"
            description="What kind of place this is — silo, warehouse, bin, etc. — used to decide which screens (e.g. silo readings) this location shows up on."
            data={[...LOCATION_TYPES]}
            required
            {...form.getInputProps('type')}
          />
          <Select
            label="Parent location (optional)"
            description="e.g. a BIN's parent WAREHOUSE"
            data={parentOptions}
            clearable
            searchable
            {...form.getInputProps('parentId')}
          />
          <Switch
            label="Active"
            description="Inactive locations are hidden from new dispatches, receipts, transfers and counts — existing documents keep referencing them."
            {...form.getInputProps('isActive', { type: 'checkbox' })}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {editing ? 'Save changes' : 'Create location'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export function LocationRegister() {
  const isAdmin = useIsAdmin();
  const { data: sitesPage } = useSites();
  const sites = sitesPage?.items ?? [];
  const { data, isLoading, isError } = useLocations();
  const [modalOpened, setModalOpened] = useState(false);
  const [editing, setEditing] = useState<LocationDto | null>(null);
  const rows = data?.items ?? [];
  const siteName = new Map(sites.map((s) => [s.id, s.code]));

  function openCreate() {
    setEditing(null);
    setModalOpened(true);
  }
  function openEdit(row: LocationDto) {
    setEditing(row);
    setModalOpened(true);
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3}>Locations</Title>
        {isAdmin && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate} size="sm" disabled={sites.length === 0}>
            New location
          </Button>
        )}
      </Group>

      {sites.length === 0 && (
        <Alert color="amber" variant="light">
          Create a site first — locations belong to a site.
        </Alert>
      )}

      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load locations.
        </Alert>
      )}

      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={640}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Site</Table.Th>
                <Table.Th>Code</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{siteName.get(row.siteId) ?? row.siteId}</Table.Td>
                  <Table.Td>{row.code}</Table.Td>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>{row.type}</Table.Td>
                  <Table.Td>
                    <Badge color={row.isActive ? 'brandGreen' : 'gray'} variant="light">
                      {row.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {isAdmin && (
                      <Button variant="subtle" size="xs" onClick={() => openEdit(row)}>
                        Edit
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="md">
                      No locations yet.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <LocationFormModal
        key={editing?.id ?? 'new'}
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        editing={editing}
        locations={rows}
      />
    </Stack>
  );
}
